/**
 * SOLOMON — Memory Panel
 * Day 7: Shows embedding coverage, triggers backfill, displays live progress.
 *
 * CONTROLLED (2026-07-28). It holds nothing but view state, so it UNMOUNTS on
 * close — the deliberate opposite of LearnPanel, which owns an in-flight
 * transcription and therefore stays mounted-hidden in App.jsx. Do not make the
 * two uniform.
 */

import { useMemoryStatus } from '../../hooks/useMemoryStatus';
import ToolModal from '../VisualTools/ToolModal';
import './memory.css';

export default function MemoryPanel({ isOpen, onClose }) {
  // Gate BEFORE the body mounts. useMemoryStatus runs a 30s poll for as long
  // as it is mounted, so a closed panel that merely hid its markup would keep
  // polling forever — hence the unmount, and hence the body being its own
  // component rather than an early return under the hook.
  if (!isOpen) return null;

  return (
    <ToolModal title="Solomon's memory" onClose={onClose}>
      <MemoryPanelBody />
    </ToolModal>
  );
}

function MemoryPanelBody() {
  const { status, isBackfilling, error, startBackfill } = useMemoryStatus();

  // Derived values. The top-level counts are MESSAGES ONLY — the bar, the
  // percent and the backfill button all bind to them, and backfillEmbeddings
  // scans live_messages only. Knowledge-item coverage arrives separately under
  // `knowledgeItems` and is REPORT-ONLY here — see the comment on the rows below.
  const percent   = status?.percent     ?? 0;
  const embedded  = status?.embedded    ?? 0;
  const pending   = status?.pending     ?? 0;
  const progress  = status?.backfill;

  const knowledge   = status?.knowledgeItems ?? null;
  const kiUnindexed = knowledge?.pending ?? 0;

  // The banner claims "RAG active", and RAG retrieves knowledge items too —
  // searchKnowledgeItems requires `embedding IS NOT NULL`, so an unindexed
  // knowledge item is invisible to it. Messages being at 100% is therefore not
  // sufficient to make that claim true.
  const isReady   = percent === 100 && kiUnindexed === 0;

  return (
    <div className="memory-panel-body">
      {status ? (
        <>
          <div className="memory-stat-row">
            <span className="memory-stat-label">Memories indexed</span>
            <span className="memory-stat-value">{embedded.toLocaleString()}</span>
          </div>

          {pending > 0 && (
            <div className="memory-stat-row">
              <span className="memory-stat-label">Pending</span>
              <span className="memory-stat-value memory-stat-value--amber">{pending.toLocaleString()}</span>
            </div>
          )}

          {/* Learned content (knowledge_items) — REPORT ONLY, deliberately no
              action affordance. The button below calls backfillEmbeddings, which
              scans live_messages only, so wiring these counts to it would offer a
              repair it never performs. These rows DO get repaired — by
              autoBackfillEmbeddings (server.js:430), which runs 30s after every
              boot — just not from here. "Not indexed" rather than "Pending" says
              exactly that: a state to know about, not a queue you can drain. */}
          {knowledge && (
            <>
              <div className="memory-stat-row">
                <span className="memory-stat-label">Learned content indexed</span>
                <span className="memory-stat-value">{knowledge.embedded.toLocaleString()}</span>
              </div>

              {kiUnindexed > 0 && (
                <div className="memory-stat-row">
                  <span className="memory-stat-label">Learned content not indexed</span>
                  <span className="memory-stat-value memory-stat-value--amber">{kiUnindexed.toLocaleString()}</span>
                </div>
              )}
            </>
          )}

          {/* Coverage bar */}
          <div className="memory-bar-wrap">
            <div
              className={`memory-bar-fill ${isBackfilling ? 'memory-bar-fill--animated' : ''}`}
              style={{ width: `${Math.max(percent, isBackfilling ? 5 : 0)}%` }}
            />
          </div>
          <div className="memory-bar-label">
            {isBackfilling
              ? `Indexing… ${progress?.processed ?? 0} / ${progress?.total ?? '?'}`
              : `${percent}% indexed`}
          </div>

          {/* Backfill controls */}
          {!isBackfilling && pending > 0 && (
            <button
              className="memory-backfill-btn"
              onClick={() => { startBackfill(); }}
            >
              Index {pending.toLocaleString()} new {pending === 1 ? 'memory' : 'memories'}
            </button>
          )}

          {isBackfilling && (
            <div className="memory-backfill-status">
              <SpinnerIcon />
              <span>Indexing memories… this may take a moment</span>
            </div>
          )}

          {isReady && !isBackfilling && (
            <div className="memory-all-good">
              ✓ All memories indexed — RAG active
            </div>
          )}
        </>
      ) : (
        <div className="memory-loading">Loading memory status…</div>
      )}

      {error && (
        <div className="memory-error">{error}</div>
      )}

      <div className="memory-dropdown-footer">
        Semantic search · 1536-dim embeddings
      </div>
    </div>
  );
}

// ── icons ─────────────────────────────────────────────────────

export function BrainIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96-.44 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24 2.5 2.5 0 0 1 4.44-1.16Z" />
      <path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96-.44 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-4.44-1.16Z" />
    </svg>
  );
}

function SpinnerIcon() {
  return (
    <svg className="memory-spinner" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
  );
}
